const createMilestones = () => ({ access: false, client: false, case: false });

export const createCaseLoadTracker = ({
  schedule = (callback) => setTimeout(callback, 150),
  cancel = (timer) => clearTimeout(timer),
} = {}) => {
  const listeners = new Set();
  const pending = new Set();
  let nextRequestId = 0;
  let idleTimer = null;
  let snapshot = {
    clientId: null,
    caseId: null,
    generation: 0,
    isOpening: false,
    pageMounted: false,
    pending: 0,
    milestones: createMilestones(),
  };
  const publish = (changes) => {
    snapshot = { ...snapshot, ...changes };
    listeners.forEach((listener) => listener());
  };
  const cancelIdle = () => {
    if (idleTimer !== null) cancel(idleTimer);
    idleTimer = null;
  };
  const checkIdle = () => {
    cancelIdle();
    if (!snapshot.isOpening || !snapshot.pageMounted || pending.size) return;
    const generation = snapshot.generation;
    // Let response handlers commit their state and start dependent requests.
    idleTimer = schedule(() => {
      idleTimer = null;
      if (
        snapshot.generation === generation &&
        snapshot.isOpening &&
        snapshot.pageMounted &&
        !pending.size
      ) {
        publish({ isOpening: false });
      }
    });
  };

  return {
    getSnapshot: () => snapshot,
    subscribe: (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    begin: (clientId, caseId) => {
      cancelIdle();
      pending.clear();
      publish({
        clientId: String(clientId),
        caseId: String(caseId),
        generation: snapshot.generation + 1,
        isOpening: true,
        pageMounted: false,
        pending: 0,
        milestones: createMilestones(),
      });
      return snapshot.generation;
    },
    reset: () => {
      cancelIdle();
      pending.clear();
      publish({
        clientId: null,
        caseId: null,
        generation: snapshot.generation + 1,
        isOpening: false,
        pageMounted: false,
        pending: 0,
        milestones: createMilestones(),
      });
    },
    completeMilestone: (milestone, generation) => {
      if (
        generation !== snapshot.generation ||
        !snapshot.isOpening ||
        !Object.hasOwn(snapshot.milestones, milestone) ||
        snapshot.milestones[milestone]
      )
        return;
      publish({ milestones: { ...snapshot.milestones, [milestone]: true } });
    },
    startRequest: () => {
      const request = {
        id: ++nextRequestId,
        generation: snapshot.generation,
        clientId: snapshot.clientId,
        caseId: snapshot.caseId,
      };
      if (snapshot.isOpening) {
        cancelIdle();
        pending.add(request.id);
        publish({ pending: pending.size });
      }
      return request;
    },
    finishRequest: (request) => {
      if (request?.generation !== snapshot.generation || !pending.delete(request.id)) return;
      publish({ pending: pending.size });
      checkIdle();
    },
    isCurrentRequest: (request) => request?.generation === snapshot.generation,
    pageMounted: (generation) => {
      if (generation !== snapshot.generation || !snapshot.isOpening) return;
      publish({ pageMounted: true });
      checkIdle();
    },
  };
};

export const caseLoadTracker = createCaseLoadTracker();
